import { useCallback, useEffect, useRef } from 'react'
import { nanoid } from 'nanoid'
import type { UIMessage } from 'ai'
import { ChatThreadManager, getMessageContent } from '../ChatThreadManager'
import { apiEndpoints } from '@/lib/env'

interface ChatThreadTitleProps {
  threadId: string | null
  threadName: string | null
  messages: UIMessage[]
  chatStatus?: 'streaming' | 'awaiting_message' | 'in_progress' | 'error' | 'ready'
  onUpdateThreadName: (params: { threadId: string; name: string }) => Promise<void>
  onClick?: () => void
}

export function ChatThreadTitle({
  threadId,
  threadName,
  messages,
  chatStatus,
  onUpdateThreadName,
  onClick,
}: ChatThreadTitleProps) {
  const hasSuggestedRef = useRef<Set<string>>(new Set())

  const suggestThreadName = useCallback(
    async () => {
      try {
        if (!threadId) {
          return
        }

        // Guard: Check if already suggested for this thread
        if (hasSuggestedRef.current.has(threadId)) {
          console.log('>>> skip suggest thread name - already suggested for this thread')
          return
        }

        const thread = await ChatThreadManager.getThread(threadId)
        if (
          !thread ||
          !messages ||
          messages.length < 2
        ) {
          return
        }

        // Use the opening exchange so short user prompts can be titled with
        // context from the assistant's response.
        const firstUserMsg = messages.find((m) => m.role === 'user')
        const firstAssistantMsg = messages.find((m) => m.role === 'assistant')
        if (!firstUserMsg || !firstAssistantMsg) return
        const userContent = getMessageContent(firstUserMsg)
        const assistantContent = getMessageContent(firstAssistantMsg)
        if (!userContent || !assistantContent) return
        const cleanUserContent = userContent
          .replace(/\s*\[[\w-]+\]\s*/g, ' ')
          .trim()
        const initialThreadName =
          cleanUserContent.length > 40
            ? cleanUserContent.slice(0, 37) + '...'
            : cleanUserContent || 'New Chat'

        // Only suggest for unnamed/default threads or the local placeholder
        // assigned before the assistant response completed.
        const isDefaultName =
          !thread.name ||
          thread.name.toLowerCase() === 'new chat' ||
          thread.name === 'Screenata' ||
          thread.name === initialThreadName
        if (!isDefaultName) {
          return
        }

        // Guard: Check if last message is from assistant (response complete)
        const lastMessage = messages[messages.length - 1]
        if (lastMessage?.role !== 'assistant') {
          console.log('>>> skip suggest thread name - last message not from assistant')
          return
        }

        // Guard: Check if chat is not streaming
        if (chatStatus === 'streaming' || chatStatus === 'in_progress') {
          console.log('>>> skip suggest thread name - chat is streaming/in_progress')
          return
        }

        // Claim before starting the request so rerenders cannot launch a duplicate.
        if (hasSuggestedRef.current.has(threadId)) return
        hasSuggestedRef.current.add(threadId)
        const token = localStorage.getItem('auth:token')
        const workspaceId = localStorage.getItem('auth:activeWorkspaceId')
        const response = await fetch(
          `${apiEndpoints.api}/ai/suggest-thread-name`,
          {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
              'X-Request-Id': nanoid(),
              'X-Workspace-Id': workspaceId!,
            },
            body: JSON.stringify({
              messages: [
                { role: 'user', content: userContent.slice(0, 600) },
                { role: 'assistant', content: assistantContent.slice(0, 1200) },
              ],
            }),
          }
        )

        if (!response.ok) {
          // do nothing, ignore error
          console.error('Failed to suggest thread name:', response.status)
          return
        }

        const data = await response.json()
        console.log('>>> suggest thread name response:', data)
        const suggestedName = data.threadName.trim()
        // assign thread id to the thread name
        if (suggestedName) {
          console.log('>>> calling updateThreadName with:', {
            threadId,
            name: suggestedName,
          })
          await onUpdateThreadName({ threadId: threadId, name: suggestedName })
          console.log('>>> updateThreadName completed')

          // Mark as suggested to prevent future calls
          hasSuggestedRef.current.add(threadId)
        } else {
          console.log('>>> no suggested name provided:', suggestedName)
        }
      } catch (error) {
        console.error('Failed to suggest thread name:', error)
      }
    },
    [threadId, messages, chatStatus, onUpdateThreadName]
  )

  // Auto-suggest thread name when response completes
  useEffect(() => {
    if (
      messages.length >= 2 &&
      chatStatus === 'ready'
    ) {
      suggestThreadName()
    }
  }, [messages.length, chatStatus, suggestThreadName])

  return (
    <button
      onClick={onClick}
      className="max-w-[240px] truncate text-sm leading-6 font-medium text-left hover:opacity-80 transition-opacity"
    >
      {threadName?.trim() || 'New chat'}
    </button>
  )
}
